import { useCallback, useEffect, useRef, useState } from 'react';
import { rulesApi } from '../utils/rulesApi';
import { useAuth } from './useAuth';
import { useFeatures } from './useFeatures';

export type RuleTenantStatusPhase = 'disabled' | 'loading' | 'ready' | 'unavailable';

/** Display-normalized rate-ceiling state; absent optional API fields render as null. */
export interface RuleRateCeilingStatus {
  present: boolean;
  tripped: boolean;
  maxPerWindow: number | null;
  windowSeconds: number | null;
  trippedAt: string | null;
  epoch: number | null;
}

export interface RuleTenantStatusState {
  phase: RuleTenantStatusPhase;
  status: RuleRateCeilingStatus | null;
  refresh: () => void;
}

const DEFAULT_POLL_MS = 60_000;

/**
 * Single feature-gated source of the tenant rate-ceiling state (ADR-022 §9), shared by the
 * persistent banner and the Rules admin page.
 *
 * Guarantees:
 * - Zero requests when `rulesEnabled` is off, the user is unauthenticated, or there is no tenant.
 * - Aborts in flight on unmount and on tenant change; resets stale state on tenant change.
 * - Never overlaps polls (a fresh poll aborts the previous one).
 * - Reports `unavailable` (not silently healthy) when the enabled endpoint fails.
 */
export const useRuleTenantStatus = (pollMs: number = DEFAULT_POLL_MS): RuleTenantStatusState => {
  const { rulesEnabled } = useFeatures();
  const { isAuthenticated, user } = useAuth();
  const tenantId = user?.tenantId;
  const active = Boolean(rulesEnabled) && isAuthenticated && Boolean(tenantId);

  const [phase, setPhase] = useState<RuleTenantStatusPhase>(active ? 'loading' : 'disabled');
  const [status, setStatus] = useState<RuleRateCeilingStatus | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken(token => token + 1), []);

  useEffect(() => {
    if (!active) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      setPhase('disabled');
      setStatus(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const raw = await rulesApi.rateCeiling(controller.signal);
        if (cancelled || controller.signal.aborted) {
          return;
        }
        // Normalize at the boundary: the banner mounts OUTSIDE the page ErrorBoundary, so a
        // malformed/partial DTO must never throw during render.
        setStatus({
          present: raw?.present === true,
          tripped: raw?.tripped === true,
          maxPerWindow: raw?.maxPerWindow ?? null,
          windowSeconds: raw?.windowSeconds ?? null,
          trippedAt: raw?.trippedAt ?? null,
          epoch: raw?.epoch ?? null,
        });
        setPhase('ready');
      } catch (error) {
        if (cancelled || rulesApi.isCanceled(error)) {
          return;
        }
        // Enabled but unreachable: surface "unavailable" rather than implying healthy.
        setStatus(null);
        setPhase('unavailable');
      } finally {
        if (!cancelled && pollMs > 0) {
          timer = setTimeout(poll, pollMs);
        }
      }
    };

    // Reset stale tenant state before the first poll of a (possibly new) tenant.
    setPhase('loading');
    setStatus(null);
    void poll();

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [active, tenantId, pollMs, refreshToken]);

  return { phase, status, refresh };
};
